import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LogOut, Settings, User, BarChart3, Upload, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

export default function Navbar() {
  const { currentUser, userProfile, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/auth');
    } catch (error) {
      toast.error('Failed to logout');
    }
  };

  return (
    <nav className="sticky top-0 z-50 glass border-b border-white/50" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/feed" className="flex items-center space-x-2" data-testid="logo-link">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-400 to-cyan-500 flex items-center justify-center">
              <span className="text-white font-bold text-xl">CT</span>
            </div>
            <span className="text-2xl font-bold gradient-text">Coder Trade</span>
          </Link>

          <div className="hidden md:flex items-center space-x-6">
            <Link
              to="/feed"
              className="text-gray-700 hover:text-sky-600 font-medium transition-colors"
              data-testid="nav-feed"
            >
              Feed
            </Link>
            <Link
              to="/upload"
              className="text-gray-700 hover:text-sky-600 font-medium transition-colors"
              data-testid="nav-upload"
            >
              Upload Project
            </Link>
            <Link
              to="/chat"
              className="text-gray-700 hover:text-sky-600 font-medium transition-colors"
              data-testid="nav-chat"
            >
              Chat
            </Link>
            <Link
              to="/dashboard"
              className="text-gray-700 hover:text-sky-600 font-medium transition-colors"
              data-testid="nav-dashboard"
            >
              Dashboard
            </Link>
          </div>

          <div className="flex items-center space-x-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-testid="user-avatar-button">
                  <Avatar>
                    <AvatarImage src={userProfile?.profile_picture} />
                    <AvatarFallback className="bg-gradient-to-br from-sky-400 to-cyan-500 text-white">
                      {userProfile?.full_name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="flex items-center justify-start gap-2 p-2">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium" data-testid="user-name">{userProfile?.full_name}</p>
                    <p className="text-xs text-gray-500" data-testid="user-email">{currentUser?.email}</p>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate(`/feed`)} data-testid="menu-feed">
                  <User className="mr-2 h-4 w-4" />
                  Feed
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate(`/profile/${userProfile?.uid}`)} data-testid="menu-profile">
                  <User className="mr-2 h-4 w-4" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/upload')} data-testid="menu-upload">
                  <Upload className="mr-2 h-4 w-4" />
                  Upload Project
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/chat')} data-testid="menu-chat">
                  <MessageSquare className="mr-2 h-4 w-4" />
                  Messages
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                  <BarChart3 className="mr-2 h-4 w-4" />
                  Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </nav>
  );
}